import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatToMaxFourDigits } from '@flexo/general';

const ENV = (import.meta as any).env;
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isWebSafari = ENV?.VITE_APP_IS_WEB === 'true' && isSafari;

export default function HorizontalChartUnified(props: any) {
  const ref: any = useRef(null);
  // Stores how many pixels of extra width (due to min-width) overflowed into the right separator
  // for a given visible group. This will be consumed by the first bar of the next visible group
  // to shift right accordingly and keep the visual separator.
  const separatorOverflowRef = useRef<Record<number, number>>({});
  const { t } = useTranslation();
  const ThemeStore = useSelector((state: any) => state.theme);
  const [data, setData] = useState<any>(null);
  const [customData, setCustomData] = useState<any>(null);
  const [hoveredBarKey, setHoveredBarKey] = useState<any>(null);

  const isDouble = Array.isArray(props?.data?.graphKeys?.[0]);
  const maxValue = props?.data?.maxValue || 0;
  const activeTooltip = props?.data?.activeTooltip;
  const setActiveTooltip = props?.data?.setActiveTooltip;

  const CustomTooltip: any = ({ active, payload }) => {
    if (isDouble && (activeTooltip !== hoveredBarKey + props?.data?.key)) return null;
    if (active && payload && payload.length) {
      const activePayload = payload.find(item => item.dataKey === hoveredBarKey);
      const unit = props?.data?.kpi?.find((datum: any) => datum.name === hoveredBarKey)?.unit || '';
      if (isDouble && props?.data?.combinedKeys) {
        return [props?.data?.combinedKeys[0].includes(activePayload?.dataKey), props?.data?.combinedKeys[1].includes(activePayload?.dataKey)]
          .map((x, i) => {
            if (x) {
              const firstActivePayload = payload.find(item => item.dataKey === props?.data?.combinedKeys[i][0]);
              const secondActivePayload = payload.find(item => item.dataKey === props?.data?.combinedKeys[i][1]);
              if (firstActivePayload === undefined || secondActivePayload === undefined) { return <></>; }
              return (
                <div
                  style={{
                    backgroundColor: 'var( --hive-greyscale-100 )',
                    padding: '10px',
                    borderRadius: '8px',
                    fontFamily: 'Flexo',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'row',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      borderBottom: `solid 4px ${firstActivePayload.fill}`,
                    }}
                  >
                    <strong className={`${isWebSafari ? 'font-flexo-light' : 'font-flexo-semibold'}`}>
                      {t(`widgets.horizontalChart.${props?.data?.context}.${props?.data?.combinedKeys[i][0]}`)}
                    </strong>{' '}
                    {props?.data?.context === 'economics'
                      ? `${unit} ${formatToMaxFourDigits(data[0][firstActivePayload.id], true)} `
                      : `${formatToMaxFourDigits(data[0][firstActivePayload.id])} ${unit}`}
                    {' | '}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      borderBottom: `solid 4px ${secondActivePayload.fill}`,
                    }}
                  >
                    <strong className={`${isWebSafari ? 'font-flexo-light' : 'font-flexo-semibold'}`} >
                      &nbsp;
                      {t(`widgets.horizontalChart.${props?.data?.context}.${props?.data?.combinedKeys[i][1]}`)}
                    </strong>{' '}
                    {props?.data?.context === 'economics'
                      ? `${unit} ${formatToMaxFourDigits(data[0][secondActivePayload.id], true)} `
                      : `${formatToMaxFourDigits(data[0][secondActivePayload.id])} ${unit}`}
                  </p>
                </div>
              );
            }
          });
      }
      if (activePayload) {
        return (
          <div
            style={{
              backgroundColor: 'var( --hive-greyscale-100 )',
              padding: '10px',
              borderRadius: '8px',
              fontFamily: 'Flexo',
              fontSize: 14,
            }}
          >
            <p
              style={{
                margin: 0,
                borderBottom: `solid 4px ${activePayload.fill}`,
              }}
            >
              <strong className={`${isWebSafari ? 'font-flexo-light' : 'font-flexo-semibold'}`}>
                {t(`widgets.horizontalChart.${props?.data?.context}.${activePayload.dataKey}`)}
              </strong>{' '}
              {props?.data?.context === 'economics'
                ? `${unit} ${formatToMaxFourDigits(activePayload.value, true)} `
                : `${formatToMaxFourDigits(activePayload.value)} ${unit}`}
            </p>
          </div>
        );
      }
    }
    return null;
  };

  const CustomBarShape = (_props: any) => {
    const MINIMUM_WIDTH = 8;
    const { x, y, width, height, fill, keyName, index } = _props;

    // Flatten keys order
    const flatKeys = Array.isArray(props?.data?.graphKeys?.[0])
      ? (props?.data?.graphKeys as any[]).flat()
      : (props?.data?.graphKeys || []);

    // Active keys that are present in graphKeys
    const activeKeys = (props?.data?.activeKeys || []).filter((k: string) =>
      flatKeys.includes(k)
    );

    // Groups definition priority:
    // 1) explicit groups
    // 2) combinedKeys (if non-empty)
    // 3) graphKeys if it's an array of arrays (double chart)
    // 4) a single group with all keys
    let groups: string[][] = (
      (Array.isArray(props?.data?.groups) && props?.data?.groups.length)
        ? (props?.data?.groups as string[][])
        : ((Array.isArray(props?.data?.combinedKeys) && props?.data?.combinedKeys.length)
            ? (props?.data?.combinedKeys as string[][])
            : (Array.isArray(props?.data?.graphKeys?.[0])
                ? (props?.data?.graphKeys as string[][])
                : [flatKeys]
              )
          )
    ) as string[][];

    // Special case: combinedKeys exists but is an empty array
    // Treat each key as its own group so adjacent values are separated
    if (Array.isArray(props?.data?.combinedKeys) && props?.data?.combinedKeys.length === 0) {
      if (!Array.isArray(props?.data?.graphKeys?.[0]) && flatKeys.length >= 1) {
        groups = flatKeys.map((k: string) => [k]);
      }
    }

    const groupIndex = Math.max(0, groups.findIndex((g) => g.includes(keyName)));
    const group = groups[groupIndex] || [];

    // Only positive, visible segments
    const isPositive = (k: string) => ((_props?.[k] ?? 0) as number) > 0;
    const visibleAll = flatKeys.filter((k) => activeKeys.includes(k) && isPositive(k));
    const visibleGroup = group.filter((k) => activeKeys.includes(k) && isPositive(k));

    // Mode: joined (one stack) vs separated (by groups) vs auto
    const mode: 'joined' | 'separated' | 'auto' =
      (props?.data?.groupMode as any) || 'auto';
    const useJoined = mode === 'joined' || (mode === 'auto' && groups.length === 1);

    // Round only on external ends
    const isFirstInGroup = keyName === (visibleGroup[0] || '');
    const isLastInGroup = keyName === (visibleGroup[visibleGroup.length - 1] || '');

    // Determine first/last VISIBLE groups (skip groups with zero visible value)
    const sumVisible = (g: string[]) =>
      g
        .filter((k) => activeKeys.includes(k))
        .reduce((s, k) => s + Math.max(0, Number((_props?.[k] ?? 0) as number)), 0);

    const visibleGroupFlags = groups.map((g) => sumVisible(g) > 0);
    const firstVisibleGroupIndex = visibleGroupFlags.indexOf(true);
    const lastVisibleGroupIndex = visibleGroupFlags.lastIndexOf(true);

    const roundLeft = useJoined
      ? keyName === (visibleAll[0] || '')
      : (groupIndex === firstVisibleGroupIndex && isFirstInGroup);

    const roundRight = useJoined
      ? keyName === (visibleAll[visibleAll.length - 1] || '')
      : (groupIndex === lastVisibleGroupIndex && isLastInGroup);

    // Separator between groups when both sides have visible values (active + > 0)
    const currGroupHasValue = visibleGroupFlags[groupIndex] || false;
    const prevVisibleGroupIndex = (() => {
      for (let i = groupIndex - 1; i >= 0; i--) if (visibleGroupFlags[i]) return i;
      return -1;
    })();
    const nextVisibleGroupIndex = (() => {
      for (let i = groupIndex + 1; i < groups.length; i++) if (visibleGroupFlags[i]) return i;
      return -1;
    })();
    const nextGroupHasValue = nextVisibleGroupIndex !== -1;
    const sepPx = typeof props?.data?.separatorPx === 'number' ? props?.data?.separatorPx : 3;

    // Apply per-corner rounding using CSS clip-path with border-radius shorthand
    const r = 6;
    const tl = roundLeft ? r : 0;
    const bl = roundLeft ? r : 0;
    const tr = roundRight ? r : 0;
    const br = roundRight ? r : 0;

    // Adjust geometry to create visual gap between groups by shrinking adjacent bars
    let adjX = x;
    let adjW = width;
    if (!useJoined) {
      if (isLastInGroup && groupIndex !== lastVisibleGroupIndex && currGroupHasValue && nextGroupHasValue) {
        adjW = Math.max(0, adjW - sepPx / 2);
      }
      if (isFirstInGroup && groupIndex !== firstVisibleGroupIndex && currGroupHasValue && prevVisibleGroupIndex !== -1) {
        adjX = x + sepPx / 2;
        adjW = Math.max(0, adjW - sepPx / 2);
      }
    }

    // No special-case overrides for split charts; rounding is driven solely by group edges

    // Enforce minimum width for tiny positive values (> 0) without collapsing the separator gap
    const valueForKey = Number((_props?.[keyName] ?? 0) as number);
    if (valueForKey > 0 && adjW < MINIMUM_WIDTH) {
      const delta = MINIMUM_WIDTH - adjW;
      // Identify if this segment touches a separator on either side
      const hasRightSeparator = !useJoined && isLastInGroup && groupIndex !== lastVisibleGroupIndex && currGroupHasValue && nextGroupHasValue;
      const hasLeftSeparator = !useJoined && isFirstInGroup && groupIndex !== firstVisibleGroupIndex && currGroupHasValue && prevVisibleGroupIndex !== -1;

      if (hasRightSeparator) {
        // Grow to the right (towards separator), then shift the next group's first bar by delta
        adjW = MINIMUM_WIDTH;
        if (nextVisibleGroupIndex !== -1) {
          separatorOverflowRef.current[nextVisibleGroupIndex] = (separatorOverflowRef.current[nextVisibleGroupIndex] || 0) + delta;
        }
      } else {
        // Default: keep left edge fixed and grow rightwards; no extra handling needed
        // This includes hasLeftSeparator-only case and joined stacks
        adjW = MINIMUM_WIDTH;
      }
    }

    // If we are the first visible key of a visible group that follows a group
    // which was expanded to min width, shift this bar to the right by the pending overflow
    if (!useJoined && isFirstInGroup && currGroupHasValue && prevVisibleGroupIndex !== -1) {
      const pendingShift = separatorOverflowRef.current[groupIndex] || 0;
      if (pendingShift > 0) {
        adjX = adjX + pendingShift;
        separatorOverflowRef.current[groupIndex] = 0;
      }
    }

    // Clamp geometry to the chart canvas width to avoid overflow
    const containerEl: any = (ref as any)?.current?.container || (ref as any)?.current;
    const containerWidth: number =
      (containerEl && (containerEl.clientWidth || containerEl.offsetWidth)) || 0;
    if (containerWidth > 0) {
      // Prevent negative X
      if (adjX < 0) {
        const lost = Math.abs(adjX);
        adjX = 0;
        adjW = Math.max(0, adjW - lost);
      }
      const maxRight = containerWidth;
      const right = adjX + adjW;
      if (right > maxRight) {
        adjW = Math.max(0, maxRight - adjX);
      }
    }

    // Build clipPath once after geometry adjustments
    const clipPathStyle = `inset(0 round ${tl}px ${tr}px ${br}px ${bl}px)`;


    

    //     // eslint-disable-next-line no-console
    //     console.group(`[UnifiedBar] ${keyName} = ${valueForKey}`);
    //     // eslint-disable-next-line no-console
    //     console.log({
    //       x,
    //       y,
    //       width,
    //       height,
    //       adjX,
    //       adjW,
    //       groupIndex,
    //       firstVisibleGroupIndex,
    //       lastVisibleGroupIndex,
    //       isFirstInGroup,
    //       isLastInGroup,
    //       useJoined,
    //       roundLeft,
    //       roundRight,
    //       tl,
    //       tr,
    //       bl,
    //       br,
    //       sepPx,
    //       currGroupHasValue,
    //       nextGroupHasValue,
    //       clipPath: clipPathStyle,
    //       fill,
    //     });
    //     // eslint-disable-next-line no-console
    //     console.groupEnd();

    return (
      <g>
        <rect
          x={adjX}
          y={y}
          width={adjW}
          height={height}
          fill={fill}
          data-key={keyName}
          data-group-index={groupIndex}
          data-first-in-group={String(isFirstInGroup)}
          data-last-in-group={String(isLastInGroup)}
          data-round-left={String(roundLeft)}
          data-round-right={String(roundRight)}
          data-clip={clipPathStyle}
          style={{ clipPath: clipPathStyle }}
        />
      </g>
    );
  };

  useEffect(() => {
    if (props?.data?.kpi && props?.data?.graphKeys) {
      if (!isDouble) {
        const _data = props.data.kpi
          .filter((datum: any) => (props.data.activeKeys.includes(datum.name) && datum?.value !== null))
          .reduce(
            (prev: any, next: any) => ({
              ...prev,
              [next.name]: next.value,
            }),
            { name: t(props.data.rankingKpiName) }
          );
        setData([_data]);
      } else {
        const _data = props.data.kpi
          .filter((datum: any) => (props.data.activeKeys.includes(datum.name) && datum?.value !== null))
          .reduce(
            (prev: any, next: any) => ({
              ...prev,
              [next.name]: next.value,
            }),
            { name: t(props.data.rankingKpiName) }
          );
        setCustomData([{
          ..._data,
          [props?.data?.combinedKeys[props?.data?.combinedKeysIndex][1]]: _data[props?.data?.combinedKeys[props?.data?.combinedKeysIndex][1]] - _data[props?.data?.combinedKeys[props?.data?.combinedKeysIndex][0]]
        }]);
        setData([_data]);
      }
    }
  }, [props.data.kpi, props.data.activeKeys]);

  try {

    
    return data ? (
      <ResponsiveContainer width={'90%'} height={20} ref={ref}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 4, right: 0, left: -50, bottom: 4 }}
        >
          <XAxis type="number" hide axisLine={false} tick={false} label={''} domain={[0, maxValue || 'auto']} />
          <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={false} label={''} />
          <Tooltip isAnimationActive={false} content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
          {(isDouble ? (props?.data?.graphKeys || []).flat() : props?.data?.graphKeys || []).map((key: string, index: number, array: any) => (
            <Bar
              isAnimationActive
              key={key}
              id={key}
              dataKey={key}
              stackId={props.data.rankingKpiName}
              onMouseEnter={() => setHoveredBarKey(key)}
              onMouseLeave={() => setHoveredBarKey(null)}
              fill={`${ThemeStore?.colors[props?.data?.colors[index].color]}${props.data?.colors[index].colorShade}`}
              shape={(barProps: any) => (
                <CustomBarShape
                  {...barProps}
                  keyName={barProps.dataKey}
                  index={index}
                  array={array}
                />
              )}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    ) : null;
  } catch (error) {
    console.log('error', error);
    return null;
  }
} 